import { Input, LucideIcon, Search, X } from "@ews-admin/global-design-system";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Generic interface for searchable entities
export interface SearchableEntity {
  id: string;
  // Same shape as the design system's SearchableEntity, so callers can move
  // between the two: search results carry nested objects (a facility's
  // location, its opening hours) alongside the scalar fields.
  [key: string]: unknown;
}

interface SearchAutocompleteProps<T extends SearchableEntity> {
  onSelect: (id: string) => void;
  selectedId: string;
  searchFunction: (keyword: string) => Promise<T[]>;
  getEntityById?: (id: string) => Promise<T>;
  getDisplayValue: (entity: T) => string;
  getSecondaryText?: (entity: T) => string | null;
  placeholder: string;
  icon?: LucideIcon;
  disabled?: boolean;
  minSearchLength?: number;
  debounceTime?: number;
}

export function SearchAutocomplete<T extends SearchableEntity>({
  onSelect,
  selectedId,
  searchFunction,
  getEntityById,
  getDisplayValue,
  getSecondaryText,
  placeholder,
  icon = Search,
  disabled = false,
  minSearchLength = 2,
  debounceTime = 300,
}: SearchAutocompleteProps<T>) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [entities, setEntities] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<T | null>(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track if this is an initial load or a user-initiated search
  const isUserSearch = useRef(false);

  // Only fetch the selected entity by ID once on initial load
  useEffect(() => {
    const fetchSelectedEntity = async () => {
      // Only fetch if we have a selectedId, getEntityById function, and haven't loaded yet
      if (!selectedId || !getEntityById || initialLoadDone) return;

      setIsLoading(true);
      try {
        const entity = await getEntityById(selectedId);
        if (entity) {
          setSelectedEntity(entity);
          setSearchTerm(getDisplayValue(entity));
        }
      } catch (error) {
        console.error("Error fetching selected entity:", error);
      } finally {
        setIsLoading(false);
        setInitialLoadDone(true);
      }
    };

    fetchSelectedEntity();
  }, [selectedId, getEntityById, getDisplayValue, initialLoadDone]);

  // Implement debounce for search term - only for user-initiated searches
  useEffect(() => {
    // Skip debounce logic if this is just setting the initial value
    if (!isUserSearch.current) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (searchTerm.trim().length >= minSearchLength) {
      debounceTimerRef.current = setTimeout(() => {
        setDebouncedSearchTerm(searchTerm);
      }, debounceTime);
    } else {
      setDebouncedSearchTerm("");
      setEntities([]);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm, minSearchLength, debounceTime]);

  // Fetch entities when debounced search term changes - only for user searches
  useEffect(() => {
    const fetchEntities = async () => {
      if (
        !debouncedSearchTerm ||
        debouncedSearchTerm.length < minSearchLength
      ) {
        return;
      }

      setIsLoading(true);
      try {
        const results = await searchFunction(debouncedSearchTerm);
        setEntities(results);
      } catch (error) {
        console.error("Error fetching search results:", error);
      } finally {
        setIsLoading(false);
      }
    };

    // Only fetch if this is a user-initiated search
    if (isUserSearch.current) {
      fetchEntities();
    }
  }, [debouncedSearchTerm, searchFunction, minSearchLength]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Mark this as a user-initiated search
    isUserSearch.current = true;

    setSearchTerm(value);

    if (selectedEntity) {
      setSelectedEntity(null);
      onSelect("");
    }

    setShowDropdown(value.trim().length >= minSearchLength);
  };

  const handleEntitySelect = (entity: T) => {
    // This is a user selection, not an API fetch
    isUserSearch.current = false;

    setSelectedEntity(entity);
    setSearchTerm(getDisplayValue(entity));
    onSelect(entity.id);
    setShowDropdown(false);
  };

  const handleClearSelection = () => {
    // This is a user action, but not a search
    isUserSearch.current = false;

    setSelectedEntity(null);
    setSearchTerm("");
    onSelect("");
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <Input
          type="text"
          placeholder={`${placeholder} ${t("common.minCharacters", {
            count: minSearchLength,
          })}`}
          value={selectedEntity ? "" : searchTerm}
          onChange={handleSearchChange}
          onFocus={() =>
            searchTerm.length >= minSearchLength &&
            isUserSearch.current &&
            setShowDropdown(true)
          }
          disabled={disabled}
          autoComplete="off"
          name="search-autocomplete"
          leftIcon={React.createElement(icon, { className: "w-4 h-4" })}
        />

        {selectedEntity && (
          <button
            type="button"
            className="flex absolute inset-y-0 right-0 items-center pr-3"
            onClick={handleClearSelection}
            disabled={disabled}
          >
            <X className="w-5 h-5 text-gray-400 hover:text-gray-500" />
          </button>
        )}
      </div>

      {/* Selected Entity Display */}
      {selectedEntity && (
        <div className="p-4 mt-3 rounded-lg border bg-neutral-50 border-neutral-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              {(selectedEntity as { photoUrl?: string }).photoUrl ? (
                <img
                  src={(selectedEntity as { photoUrl?: string }).photoUrl!}
                  alt={getDisplayValue(selectedEntity)}
                  className="object-cover mr-3 w-10 h-10 rounded-full"
                />
              ) : (
                React.createElement(icon, {
                  className: "w-10 h-10 text-neutral-400 mr-3",
                })
              )}
              <div>
                <div className="font-medium text-neutral-900">
                  {getDisplayValue(selectedEntity)}
                </div>
                {getSecondaryText && getSecondaryText(selectedEntity) && (
                  <div className="text-sm text-neutral-500">
                    {getSecondaryText(selectedEntity)}
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={handleClearSelection}
              className="transition-colors text-neutral-400 hover:text-neutral-600"
              disabled={disabled}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {showDropdown && entities?.length > 0 && (
        <div className="overflow-auto absolute z-10 mt-1 w-full max-h-60 bg-white rounded-md shadow-lg">
          <ul className="py-1">
            {entities.map(entity => (
              <li
                key={entity.id}
                className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                onClick={() => handleEntitySelect(entity)}
              >
                <div className="flex items-center">
                  {React.createElement(icon, {
                    className: "w-4 h-4 text-gray-400",
                  })}
                  <div className="ml-2">
                    <div className="font-medium">{getDisplayValue(entity)}</div>
                    {getSecondaryText && getSecondaryText(entity) && (
                      <div className="text-xs text-gray-500">
                        {getSecondaryText(entity)}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showDropdown &&
        debouncedSearchTerm &&
        entities?.length === 0 &&
        !isLoading && (
          <div className="absolute z-10 p-4 mt-1 w-full text-center text-gray-500 bg-white rounded-md shadow-lg">
            {t("common.noSearchResults")}
          </div>
        )}

      {isLoading && (
        <div className="absolute z-10 p-4 mt-1 w-full text-center text-gray-500 bg-white rounded-md shadow-lg">
          {t("common.loading")}
        </div>
      )}
    </div>
  );
}
